import * as React from 'react'
import { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import {
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { Form, required, useTranslate, useLogin, useNotify, useSafeSetState } from 'ra-core'
import { TextInput, email } from 'react-admin'

import { requestResetPassword } from './dataProvider'
import { handleAdminError } from './utils/errorHandler'

const resetEmailValidator = email('Please enter a valid email address')

export const LoginForm = (props) => {
  const { redirectTo, className } = props
  const [isMfaForm, setIsMfaForm] = useSafeSetState(false)
  const [attemptLoginToken, setAttemptLoginToken] = useSafeSetState(null)
  const [loading, setLoading] = useSafeSetState(false)
  const [forgotOpen, setForgotOpen] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetEmailError, setResetEmailError] = useState('')
  const [resetSending, setResetSending] = useState(false)
  const login = useLogin()
  const translate = useTranslate()
  const notify = useNotify()

  useEffect(() => {
    if (isMfaForm) {
      setForgotOpen(false)
    }
  }, [isMfaForm])

  const submit2FA = (values) => {
    setLoading(true)
    delete values.email
    delete values.password
    const params = { type: '2fa', code: values.code, token: attemptLoginToken }
    login(params, redirectTo)
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)
        notify(
          typeof error === 'string'
            ? error
            : typeof error === 'undefined' || !error.message
            ? 'ra.auth.sign_in_error'
            : error.message,
          {
            type: 'error',
            messageArgs: {
              _:
                typeof error === 'string'
                  ? error
                  : error && error.message
                  ? error.message
                  : undefined,
            },
          }
        )
      })
  }

  const submit = (values) => {
    setLoading(true)
    values.type = 'email'
    login(values, redirectTo)
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)
        if (error.isMFARequired) {
          setAttemptLoginToken(error.token)
          setIsMfaForm(true)
        } else {
          notify(
            typeof error === 'string'
              ? error
              : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
            {
              type: 'error',
              messageArgs: {
                _:
                  typeof error === 'string'
                    ? error
                    : error && error.message
                    ? error.message
                    : undefined,
              },
            }
          )
        }
      })
  }

  const closeForgotDialog = () => {
    if (resetSending) {
      return
    }
    setForgotOpen(false)
    setResetEmail('')
    setResetEmailError('')
  }

  const sendResetRequest = async () => {
    const trimmed = resetEmail.trim()
    if (!trimmed) {
      setResetEmailError('Please enter your email')
      return
    }
    const err = resetEmailValidator(trimmed)
    setResetEmailError(err || '')
    if (err) {
      return
    }
    setResetSending(true)
    try {
      await requestResetPassword({ email: trimmed })
      notify(
        'If an account exists for this email, we sent a message with a link to reset your password.',
        { type: 'success' }
      )
      setForgotOpen(false)
      setResetEmail('')
    } catch (error) {
      notify(handleAdminError(error), { type: 'error' })
    } finally {
      setResetSending(false)
    }
  }

  return (
    <>
      {isMfaForm ? (
        <StyledForm onSubmit={submit2FA} mode="onChange" noValidate className={className}>
          <CardContent className={LoginFormClasses.content}>
            <TextInput
              autoFocus
              source="code"
              label="OTP"
              type="number"
              validate={required()}
              fullWidth
            />
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              fullWidth
              className={LoginFormClasses.button}
            >
              {loading ? (
                <CircularProgress className={LoginFormClasses.icon} size={19} thickness={3} />
              ) : (
                translate('ra.auth.sign_in')
              )}
            </Button>
          </CardContent>
        </StyledForm>
      ) : (
        <StyledForm onSubmit={submit} mode="onChange" noValidate className={className}>
          <CardContent className={LoginFormClasses.content}>
            <TextInput
              autoFocus
              source="email"
              label="Email"
              type="email"
              validate={[required(), email('Please enter a valid email address')]}
              fullWidth
            />
            <TextInput
              source="password"
              label={translate('ra.auth.password')}
              type="password"
              autoComplete="current-password"
              validate={required()}
              fullWidth
            />

            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              fullWidth
              className={LoginFormClasses.button}
            >
              {loading ? (
                <CircularProgress className={LoginFormClasses.icon} size={19} thickness={3} />
              ) : (
                translate('ra.auth.sign_in')
              )}
            </Button>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setForgotOpen(true)
                setResetEmailError('')
              }}
              sx={{
                cursor: 'pointer',
                display: 'block',
                mt: 1.5,
                textAlign: 'center',
                width: '100%',
              }}
            >
              Forgot password?
            </Link>
          </CardContent>
        </StyledForm>
      )}
      <Dialog
        open={forgotOpen && !isMfaForm}
        onClose={(_, reason) => {
          if (resetSending && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return
          }
          closeForgotDialog()
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="forgot-password-dialog-title"
      >
        <DialogTitle id="forgot-password-dialog-title">Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your email address. If an account exists, we will send a link to reset your
            password.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Email"
            type="email"
            autoComplete="email"
            value={resetEmail}
            onChange={(e) => {
              setResetEmail(e.target.value)
              setResetEmailError('')
            }}
            error={Boolean(resetEmailError)}
            helperText={resetEmailError}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={closeForgotDialog} disabled={resetSending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={resetSending}
            onClick={sendResetRequest}
          >
            {resetSending ? (
              <CircularProgress className={LoginFormClasses.icon} size={19} thickness={3} />
            ) : (
              'Send reset link'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const PREFIX = 'RaLoginForm'

export const LoginFormClasses = {
  content: `${PREFIX}-content`,
  button: `${PREFIX}-button`,
  icon: `${PREFIX}-icon`,
}

const StyledForm = styled(Form, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  [`& .${LoginFormClasses.content}`]: {
    width: 300,
  },
  [`& .${LoginFormClasses.button}`]: {
    marginTop: theme.spacing(2),
  },
  [`& .${LoginFormClasses.icon}`]: {
    margin: theme.spacing(0.3),
  },
}))

LoginForm.propTypes = {
  redirectTo: PropTypes.string,
}
